import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';

import { Sku } from '@app/api/models/sku';
import { booleanBodyRender, dateTimeBodyRender, TableColumn } from '@app/components/common/table';

import { SkuLookups } from './types';

const STATUS_OPTIONS: EntityBase[] = [
  { id: 'active', name: 'active' },
  { id: 'inactive', name: 'inactive' },
];

const toBooleanFilter = (value: string) => value === '1';

const toDateRangeFilter = (value: string[]) =>
  value?.length ? value.map((date) => date.split('T')[0]) : undefined;

export const useColumns = ({
  lookups,
  onEdit,
  canChange,
}: {
  lookups: SkuLookups;
  onEdit: (sku: Sku) => void;
  canChange: boolean;
}): TableColumn<Sku>[] => {
  return [
    {
      name: 'id',
      label: '#',
      options: {
        filter: {
          type: 'text',
          name: 'pid',
          onApply: (value: string) => Number(value),
        },
        sort: true,
      },
    },
    {
      name: 'title',
      label: 'Наименование',
      options: {
        filter: { type: 'text', name: 'title' },
        sort: true,
      },
    },
    {
      name: 'title_en',
      label: 'Наименование (EN)',
      options: {
        hidden: true,
        filter: { type: 'text', name: 'title_en' },
        sort: true,
      },
    },
    {
      name: 'code',
      label: 'Код',
      options: {
        filter: { type: 'text', name: 'code' },
        sort: false,
      },
    },
    {
      name: 'erp_code',
      label: 'Код ERP',
      options: {
        filter: { type: 'text', name: 'erp_code' },
        sort: false,
      },
    },
    {
      name: 'uniq_code',
      label: 'Уникальный код',
      options: {
        hidden: true,
        filter: { type: 'text', name: 'uniq_code' },
        sort: false,
      },
    },
    {
      name: 'bar_code',
      label: 'Штрихкод',
      options: {
        filter: { type: 'text', name: 'bar_code' },
        sort: false,
      },
    },
    {
      name: 'ean_code',
      label: 'EAN',
      options: {
        hidden: true,
        filter: { type: 'text', name: 'ean_code' },
        sort: false,
      },
    },
    {
      name: 'trade_mark',
      label: 'Торговая марка',
      options: {
        hidden: true,
        filter: { type: 'text', name: 'trade_mark' },
        sort: true,
      },
    },
    {
      name: 'brand',
      label: 'Бренд',
      options: {
        filter: { type: 'text', name: 'brand' },
        sort: true,
      },
    },
    {
      name: 'category_id',
      label: 'Категория',
      options: {
        filter: {
          type: 'multi-select',
          name: 'category_id',
          onOptionsFetch: () => lookups.categories,
        },
        sort: true,
        customBodyRender: (value: number) => <span>{lookups.categoriesById[value] || '—'}</span>,
      },
    },
    {
      name: 'subcategory_id',
      label: 'Подкатегория',
      options: {
        filter: {
          type: 'multi-select',
          name: 'subcategory_id',
          onOptionsFetch: () => lookups.subcategories,
        },
        sort: true,
        customBodyRender: (value: number) => <span>{lookups.subcategoriesById[value] || '—'}</span>,
      },
    },
    {
      name: 'unit_id',
      label: 'Ед. изм.',
      options: {
        filter: {
          type: 'multi-select',
          name: 'unit_id',
          onOptionsFetch: () => lookups.units,
        },
        sort: true,
        customBodyRender: (value: number) => <span>{lookups.unitsById[value] || '—'}</span>,
      },
    },
    {
      name: 'is_available_for_order',
      label: 'Доступен для заказа',
      options: {
        hidden: true,
        filter: {
          type: 'boolean',
          name: 'is_available_for_order',
          onApply: toBooleanFilter,
        },
        sort: false,
        customBodyRender: booleanBodyRender(),
      },
    },
    {
      name: 'is_top',
      label: 'Топ',
      options: {
        hidden: true,
        filter: {
          type: 'boolean',
          name: 'is_top',
          onApply: toBooleanFilter,
        },
        sort: false,
        customBodyRender: booleanBodyRender(),
      },
    },
    {
      name: 'status',
      label: 'Статус',
      options: {
        hidden: true,
        filter: {
          type: 'select',
          name: 'status',
          onOptionsFetch: () => STATUS_OPTIONS,
          onApply: (value: EntityBase) => String(value.id),
        },
        sort: false,
      },
    },
    {
      name: 'created_dt',
      label: 'Дата создания',
      options: {
        hidden: true,
        filter: {
          type: 'date-range',
          name: 'created_dt',
          onApply: toDateRangeFilter,
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(),
      },
    },
    {
      name: 'updated_dt',
      label: 'Дата обновления',
      options: {
        hidden: true,
        filter: {
          type: 'date-range',
          name: 'updated_dt',
          onApply: toDateRangeFilter,
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(),
      },
    },
    {
      name: 'is_active',
      label: 'Активен',
      options: {
        filter: {
          type: 'boolean',
          name: 'is_active',
          onApply: toBooleanFilter,
        },
        sort: true,
        customBodyRender: booleanBodyRender(),
      },
    },
    ...(canChange
      ? ([
          {
            name: 'id',
            label: 'Действия',
            options: {
              filter: false,
              sort: false,
              customBodyRender: (id, { rowData }) => (
                <Flex gap={2}>
                  <Tooltip label="Редактировать">
                    <IconButton
                      name="edit"
                      onClick={() => onEdit(rowData as Sku)}
                      aria-label="edit"
                      size="sm"
                    >
                      <MdEdit />
                    </IconButton>
                  </Tooltip>
                </Flex>
              ),
            },
          },
        ] as TableColumn<Sku>[])
      : []),
  ];
};
